import React, { useRef, useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, avatarUrl } from "@/lib/api";

export default function EditProfileModal({ open, profile, address, onClose, onSaved }) {
  const [username, setUsername] = useState(profile?.username || "");
  const [displayName, setDisplayName] = useState(profile?.display_name || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [twitter, setTwitter] = useState(profile?.twitter || "");
  const [avatarPath, setAvatarPath] = useState(profile?.avatar_path || null);
  const [preview, setPreview] = useState(avatarUrl(profile?.avatar_path));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  if (!open) return null;

  const pickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      fd.append("owner", address);
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarPath(data.avatar_path);
    } catch (e) { toast.error("Avatar upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/profile", {
        address, username: username || null, display_name: displayName || null,
        bio: bio || null, twitter: twitter || null, avatar_path: avatarPath,
      });
      toast.success("Profile saved");
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md card-surface p-6 rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">Edit profile</h3>
          <button onClick={onClose} data-testid="edit-profile-close" className="text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center shrink-0">
            {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl">🙂</span>}
          </div>
          <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-button" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm hover:border-[#1EC62E]/50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickAvatar} className="hidden" data-testid="avatar-file-input" />
        </div>

        <label className="block text-xs text-muted-foreground mb-1">Username (your referral handle)</label>
        <div className="flex items-center mb-3">
          <span className="px-3 py-2.5 rounded-l-lg bg-white/5 border border-r-0 border-white/10 font-mono text-muted-foreground">@</span>
          <input value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="satoshi" data-testid="profile-username-input" maxLength={20} className="flex-1 bg-white/5 border border-white/10 rounded-r-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#1EC62E]/50" />
        </div>

        <label className="block text-xs text-muted-foreground mb-1">Display name</label>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" data-testid="profile-name-input" maxLength={40} className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1EC62E]/50" />

        <label className="block text-xs text-muted-foreground mb-1">Bio</label>
        <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Degen. Builder. Memecoin enjoyer." data-testid="profile-bio-input" maxLength={160} rows={2} className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1EC62E]/50 resize-none" />

        <label className="block text-xs text-muted-foreground mb-1">Twitter / X URL</label>
        <input value={twitter} onChange={(e) => setTwitter(e.target.value)} placeholder="https://x.com/you" data-testid="profile-twitter-input" className="w-full mb-5 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1EC62E]/50" />

        <button onClick={save} disabled={saving || uploading} data-testid="profile-save-button" className="w-full py-3 rounded-lg font-display font-bold text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  );
}
